import { useEffect, useState } from 'react'
import { isElectron } from '~/utils/common'

// AWS SigV4 rejects any request whose signature is timestamped more than five
// minutes from the server's own clock ("Signature expired"). When a user's
// machine clock drifts that far, signed requests such as the CloudWatch
// diagnostic-log upload fail silently and can't be recovered by refreshing
// credentials. Warn well before the AWS limit so the user can fix their clock.
// Sized to the 5-min SigV4 window; revisit if this signal ever gates TOTP/2FA
// (30s windows), where a sub-threshold skew would still break login.
const CLOCK_SKEW_THRESHOLD_MS = 2 * 60 * 1000
const CHECK_INTERVAL_MS = 5 * 60 * 1000

// Abort a hung request so a stalled socket (captive portal / dead proxy that
// accepts but never answers) can't leave check() pending and let the interval
// stack overlapping fetches. Mirrors AWS_TIME_REQUEST_TIMEOUT_MS on the
// Electron side (listeners.ts).
const TIME_REQUEST_TIMEOUT_MS = 5000

// The midpoint estimate can be off by up to half the round trip, so a slow
// round trip can't substantiate a 2-min skew. Round trips this long usually
// mean the device slept or the tab was frozen mid-request (the abort timer
// doesn't run while suspended), not that the clock is wrong.
const MAX_ROUND_TRIP_MS = 10_000

// A skewed reading is only reported once a follow-up reading agrees with it
// (within CLOCK_SKEW_THRESHOLD_MS, which also forces the same direction), so a
// single outlier can't raise the warning. A confirm that fires while another
// check is in flight is skipped, because that check's reading is the
// follow-up. If the follow-up is discarded, the next scheduled check serves as
// the confirmation instead, as long as it lands within MAX_CONFIRM_AGE_MS.
const CONFIRM_DELAY_MS = 15_000

// Oldest a skewed reading can be and still be confirmed, so two unrelated
// outliers hours apart (e.g. across an overnight sleep) can't pair up.
const MAX_CONFIRM_AGE_MS = CHECK_INTERVAL_MS + CONFIRM_DELAY_MS

// Floor between a usable reading and a check triggered by the tab becoming
// visible or the network coming back, so rapid tab switching doesn't fire a
// request each time. Failed checks don't start it, so `online` can recheck
// right after a wake where the first check ran before the network was back.
const MIN_EVENT_RECHECK_GAP_MS = 60_000

// On the hosted web app, version_info.json is served same-origin, so its
// `Date` response header is readable without the server CORS-exposing it.
// Root-absolute so it resolves the same from any deep-linked route (matching
// appVersionQueries.ts's `baseURL: '/'`), rather than the SPA fallback.
const WEB_TIME_SOURCE_URL = '/version_info.json'

export interface ClockSkewState {
  isClockSkewed: boolean
  // Positive = local clock ahead of server; negative = behind. null until a
  // usable server time has been read.
  skewMs: number | null
}

interface ServerTimeReading {
  serverTimeMs: number
  // Midpoint of the round trip, used to net out request latency.
  localMidpointMs: number
}

const toReading = (
  serverTimeMs: number,
  sentAt: number,
  receivedAt: number,
): ServerTimeReading | null => {
  const roundTripMs = receivedAt - sentAt
  if (roundTripMs < 0 || roundTripMs > MAX_ROUND_TRIP_MS) return null
  return { serverTimeMs, localMidpointMs: (sentAt + receivedAt) / 2 }
}

// Desktop loads from a local protocol, so the renderer can't read a server
// `Date` header. The main process (Node, no CORS) fetches AWS's own clock —
// the clock that signs our CloudWatch uploads — and returns it as epoch ms.
const readServerTimeFromMain = async (): Promise<ServerTimeReading | null> => {
  const sentAt = Date.now()
  const serverTimeMs = (await window.electron?.invoke('get-server-time')) as
    number | null | undefined
  const receivedAt = Date.now()
  if (typeof serverTimeMs !== 'number' || Number.isNaN(serverTimeMs)) {
    return null
  }
  return toReading(serverTimeMs, sentAt, receivedAt)
}

// Web: read the same-origin `Date` response header. `cache: 'no-store'` stops
// the browser's own cache, but a shared/CDN cache can still return a response
// generated earlier, whose `Date` is stale enough to trip the 2-min threshold.
// Per HTTP spec such a cache must set `Age` (seconds the response has sat in
// cache), so add it back to recover the origin's real clock.
const readServerTimeFromHeader =
  async (): Promise<ServerTimeReading | null> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIME_REQUEST_TIMEOUT_MS,
    )
    try {
      const sentAt = Date.now()
      const response = await fetch(WEB_TIME_SOURCE_URL, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      })
      const receivedAt = Date.now()
      const dateHeader = response.headers.get('date')
      if (!dateHeader) return null
      const dateMs = new Date(dateHeader).getTime()
      if (Number.isNaN(dateMs)) return null
      const ageSeconds = Number(response.headers.get('age'))
      const ageMs =
        Number.isFinite(ageSeconds) && ageSeconds > 0 ? ageSeconds * 1000 : 0
      return toReading(dateMs + ageMs, sentAt, receivedAt)
    } finally {
      clearTimeout(timeoutId)
    }
  }

/**
 * Compares the local clock against a trusted server time and reports whether
 * the two differ by more than CLOCK_SKEW_THRESHOLD_MS. On desktop the server
 * time comes from the main process (AWS clock via IPC); on web it's read from
 * the same-origin `Date` response header.
 *
 * Fail-safe: any failure to read a usable server time leaves the state
 * un-skewed, so we never warn on a guess. A skewed reading must be confirmed
 * by a follow-up reading before it is reported.
 */
export const useClockSkewCheck = (): ClockSkewState => {
  const [state, setState] = useState<ClockSkewState>({
    isClockSkewed: false,
    skewMs: null,
  })

  useEffect(() => {
    let cancelled = false
    let inFlight = false
    let lastReadingAt = 0
    let previousSkewedReading: { skewMs: number; takenAt: number } | null = null
    let confirmTimeoutId: ReturnType<typeof setTimeout> | undefined

    const check = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const reading = isElectron
          ? await readServerTimeFromMain()
          : await readServerTimeFromHeader()
        if (!reading || cancelled) return
        lastReadingAt = Date.now()

        // The Date header has one-second resolution, negligible against the
        // two-minute threshold.
        const skewMs = reading.localMidpointMs - reading.serverTimeMs
        const isClockSkewed = Math.abs(skewMs) > CLOCK_SKEW_THRESHOLD_MS
        if (!isClockSkewed) {
          previousSkewedReading = null
          clearTimeout(confirmTimeoutId)
          setState({ isClockSkewed, skewMs })
          return
        }

        const agreesWithPrevious =
          previousSkewedReading !== null &&
          Math.abs(lastReadingAt - previousSkewedReading.takenAt) <=
            MAX_CONFIRM_AGE_MS &&
          Math.abs(skewMs - previousSkewedReading.skewMs) <=
            CLOCK_SKEW_THRESHOLD_MS
        previousSkewedReading = { skewMs, takenAt: lastReadingAt }

        if (!agreesWithPrevious) {
          clearTimeout(confirmTimeoutId)
          confirmTimeoutId = setTimeout(() => void check(), CONFIRM_DELAY_MS)
          return
        }

        setState({ isClockSkewed, skewMs })
      } catch {
        // Fail safe: never surface a warning we can't substantiate.
      } finally {
        inFlight = false
      }
    }

    const recheckOnEvent = () => {
      if (document.visibilityState === 'hidden') return
      if (Math.abs(Date.now() - lastReadingAt) < MIN_EVENT_RECHECK_GAP_MS)
        return
      void check()
    }

    void check()
    const intervalId = setInterval(() => void check(), CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', recheckOnEvent)
    window.addEventListener('online', recheckOnEvent)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      clearTimeout(confirmTimeoutId)
      document.removeEventListener('visibilitychange', recheckOnEvent)
      window.removeEventListener('online', recheckOnEvent)
    }
  }, [])

  return state
}
